import React, { useEffect, useState } from 'react';
import { MapPin, Phone, Star, Video, ShieldCheck, ExternalLink, Zap, Clock, Tag, Instagram, Bell, Ticket, Mail } from 'lucide-react';
import { NearbyRecovery } from './NearbyRecovery';

export const scrollToRaceAlerts = () => {
  const el = document.getElementById('race-alerts');
  if (!el) return;
  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  setTimeout(() => el.querySelector('input[type="email"]')?.focus({ preventScroll: true }), 600);
};

const pickFileSrc = (video) => {
  if (video.webm_url && typeof document !== 'undefined') {
    const probe = document.createElement('video');
    if (probe.canPlayType('video/webm; codecs="vp9"')) return video.webm_url;
  }
  return video.mp4_url || video.webm_url;
};

export const VideoEmbed = ({ video, name }) => (
  video.provider === 'file' ? (
    <div className="w-full overflow-hidden bg-black" data-testid="gym-video-file">
      <video
        ref={(el) => { if (el) { el.muted = true; el.defaultMuted = true; } }}
        className="w-full bg-black"
        style={{ maxHeight: 480, objectFit: 'contain' }}
        src={pickFileSrc(video)}
        autoPlay
        muted
        loop
        playsInline
        controls
        preload="metadata"
        poster={video.poster}
        title={video.title || `${name} — video`}
      />
    </div>
  ) : (
    <div className="relative w-full overflow-hidden bg-black" style={{ paddingBottom: '56.25%' }}>
      <iframe
        src={video.embed_url}
        title={video.title || `${name} — video`}
        className="absolute inset-0 w-full h-full"
        loading="lazy"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </div>
  )
);

const mapsHref = (address) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;

const fmtClassDate = (d) => d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

const nextOccurrences = (classes, count = 4) => {
  const out = [];
  const now = new Date();
  for (let i = 1; i <= 21 && out.length < count; i++) {
    const d = new Date(now);
    d.setDate(now.getDate() + i);
    classes.forEach((c) => {
      if (d.getDay() === c.day && out.length < count) out.push({ date: d, ...c });
    });
  }
  return out;
};

export const UpcomingClasses = ({ gym }) => {
  const [live, setLive] = useState(null);
  useEffect(() => {
    if (!gym.live_classes) return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/directory/${gym.gym_id}/live-classes`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d?.classes?.length) setLive(d.classes); })
      .catch(() => {});
  }, [gym.gym_id, gym.live_classes]);

  if (live?.length && gym.buy_class_url) {
    return (
      <div className="mb-4" data-testid={`dfw-live-classes-${gym.gym_id}`}>
        <p className="text-zinc-500 text-[10px] uppercase tracking-[2px] mb-1.5">Upcoming Classes — Live Availability</p>
        <div className="space-y-1">
          {live.map((c) => (
            <a
              key={`${c.date}-${c.time_label}`}
              href={gym.buy_class_url}
              target="_blank"
              rel="noopener noreferrer sponsored"
              className="flex items-center justify-between gap-2 px-2.5 py-1.5 bg-black/40 border border-zinc-800 hover:border-[#C7FA3E]/60 transition-colors text-xs group"
              data-testid={`dfw-live-slot-${gym.gym_id}-${c.date}`}
            >
              <span className="text-white font-semibold whitespace-nowrap">{c.date_label} · {c.time_label}</span>
              <span className="text-zinc-500 truncate hidden sm:inline">{c.label}{c.price ? ` · ${c.price}` : ''}</span>
              <span className={`whitespace-nowrap text-[10px] uppercase tracking-wider font-bold ${c.spots > 0 ? 'text-[#C7FA3E]' : 'text-zinc-500'}`}>
                {c.spots > 0 ? `${c.spots} spot${c.spots === 1 ? '' : 's'} left` : 'Full — waitlist'}
              </span>
              <span className="text-[#C7FA3E] uppercase tracking-wider text-[10px] font-bold whitespace-nowrap group-hover:underline">Book →</span>
            </a>
          ))}
        </div>
        <p className="text-zinc-600 text-[10px] mt-1.5">Spot counts live from the gym's booking calendar</p>
      </div>
    );
  }

  if (!gym.recurring_classes?.length || !gym.buy_class_url) return null;
  return (
    <div className="mb-4" data-testid={`dfw-upcoming-classes-${gym.gym_id}`}>
      <p className="text-zinc-500 text-[10px] uppercase tracking-[2px] mb-1.5">Upcoming Classes — Book Direct</p>
      <div className="space-y-1">
        {nextOccurrences(gym.recurring_classes).map((o) => (
          <a
            key={`${o.date.toISOString()}-${o.time}`}
            href={gym.buy_class_url}
            target="_blank"
            rel="noopener noreferrer sponsored"
            className="flex items-center justify-between gap-2 px-2.5 py-1.5 bg-black/40 border border-zinc-800 hover:border-[#C7FA3E]/60 transition-colors text-xs group"
            data-testid={`dfw-class-slot-${gym.gym_id}-${o.date.getMonth() + 1}-${o.date.getDate()}`}
          >
            <span className="text-white font-semibold whitespace-nowrap">{fmtClassDate(o.date)} · {o.time}</span>
            <span className="text-zinc-500 truncate">{o.label}{o.price ? ` · ${o.price}` : ''}</span>
            <span className="text-[#C7FA3E] uppercase tracking-wider text-[10px] font-bold whitespace-nowrap group-hover:underline">Book →</span>
          </a>
        ))}
      </div>
    </div>
  );
};

export const DfwGymCard = ({ gym, onAddVideo, onClaim, locatorMetro }) => {
  const hasMedia = Boolean(gym.video || gym.photos?.length > 0);
  const visual = hasMedia || Boolean(gym.stub_image);
  return (
  <div
    id={gym.gym_id}
    className={`bg-zinc-900/50 border border-zinc-800 hover:border-zinc-700 transition-colors scroll-mt-28 ${visual ? 'lg:grid lg:grid-cols-2' : ''}`}
    data-testid={`dfw-gym-card-${gym.gym_id}`}
  >
    {/* Video / photos / stub image — right column on desktop */}
    {visual && (
    <div className="lg:order-2 lg:p-5 lg:pl-2.5 lg:h-full">
      {hasMedia ? (
      <div className={`flex flex-col gap-1.5 lg:h-full ${gym.video && !gym.photos?.length ? 'lg:justify-center' : ''}`} data-testid={`dfw-gym-photo-${gym.gym_id}`}>
          {gym.video ? (
            <VideoEmbed video={gym.video} name={gym.name} />
          ) : (
            <img
              src={gym.photos[0]}
              alt={`${gym.name} — training facility`}
              className="w-full h-52 lg:h-56 object-cover"
              loading="lazy"
            />
          )}
          {(() => {
            const rest = gym.video ? (gym.photos || []) : gym.photos.slice(1);
            if (!rest.length) return null;
            return (
              <div className={`grid gap-1.5 lg:flex-1 lg:auto-rows-fr ${rest.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
                {rest.slice(0, 4).map((p, i) => (
                  <img key={p} src={p} alt={`${gym.name} — facility ${i + 2}`} className="w-full h-36 lg:h-full lg:min-h-28 object-cover" loading="lazy" />
                ))}
              </div>
            );
          })()}
        </div>
      ) : (
        <div className="lg:h-full flex items-center" data-testid={`dfw-stub-image-${gym.gym_id}`}>
          <img src={gym.stub_image} alt={`${gym.name} — gym`} className="w-full h-52 lg:h-56 object-cover" loading="lazy" />
        </div>
      )}
    </div>
    )}

    <div className="p-4 lg:p-5 flex flex-col lg:order-1">
      {gym.logo && (
        <span className={gym.logo_bg === 'light' ? 'inline-block self-start bg-white rounded px-2.5 py-1.5 mb-3' : 'self-start mb-3'}>
          <img
            src={gym.logo}
            alt={`${gym.name} logo`}
            className="h-10 w-auto"
            loading="lazy"
            data-testid={`dfw-gym-logo-${gym.gym_id}`}
          />
        </span>
      )}
      <div className="flex items-start justify-between gap-3 mb-2">
        <h3 className="text-white text-xl leading-tight" style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '0.02em' }}>
          {gym.name}
        </h3>
        {gym.rating && (
          <span className="flex items-center gap-1 text-[#C7FA3E] text-xs whitespace-nowrap">
            <Star className="w-3 h-3 fill-current" /> {gym.rating} · {gym.review_count}
          </span>
        )}
      </div>

      {gym.badges?.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {gym.badges.map((b) => (
            <span key={b} className="px-2 py-0.5 text-[10px] uppercase tracking-wider bg-zinc-800 text-zinc-300 border border-zinc-700">
              {b}
            </span>
          ))}
        </div>
      )}

      <p className="text-zinc-400 text-sm leading-relaxed mb-3">{gym.description}</p>
      {gym.coaches?.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-3 text-xs" data-testid={`dfw-coaches-${gym.gym_id}`}>
          <span className="text-zinc-500 uppercase tracking-wider">Coaches:</span>
          {gym.coaches.map((c) => (
            c.instagram ? (
              <a key={c.name} href={c.instagram} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#C7FA3E] hover:underline">
                <Instagram className="w-3 h-3" /> {c.name}
              </a>
            ) : (
              <span key={c.name} className="text-zinc-300">{c.name}</span>
            )
          ))}
        </div>
      )}
      {gym.schedule_note && (
        <p className="text-zinc-500 text-xs mb-3 flex items-center gap-1.5">
          <Clock className="w-3 h-3 flex-shrink-0" /> {gym.schedule_note}
        </p>
      )}

      {(gym.instagram || gym.email || gym.socials?.length > 0) && (
        <p className="text-zinc-500 text-xs mb-3 flex flex-wrap items-center gap-x-3 gap-y-1" data-testid={`dfw-contact-${gym.gym_id}`}>
          {gym.instagram && (
            <a href={gym.instagram} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#C7FA3E] hover:underline">
              <Instagram className="w-3 h-3" /> @{gym.instagram.replace(/\/+$/, '').split('/').pop()}
            </a>
          )}
          {(gym.socials || []).map((s) => (
            <a key={s.url} href={s.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#C7FA3E] hover:underline">
              {s.label === 'Instagram' ? <Instagram className="w-3 h-3" /> : <ExternalLink className="w-3 h-3" />} {s.label}
            </a>
          ))}
          {gym.email && (
            <a href={`mailto:${gym.email}`} className="inline-flex items-center gap-1 hover:text-zinc-300">
              <Mail className="w-3 h-3" /> {gym.email}
            </a>
          )}
        </p>
      )}

      {gym.trial_offer && (
        <p className="inline-flex items-center gap-1.5 mb-3 px-3 py-2 text-xs text-[#C7FA3E] bg-[#C7FA3E]/[0.06] border border-[#C7FA3E]/25" data-testid={`dfw-trial-${gym.gym_id}`}>
          <Ticket className="w-3.5 h-3.5 flex-shrink-0" /> {gym.trial_offer}
        </p>
      )}

      {gym.discount_note && (
        <p className="inline-flex items-center gap-1.5 mb-3 px-3 py-2 text-xs text-[#C7FA3E] bg-[#C7FA3E]/[0.06] border border-[#C7FA3E]/25" data-testid={`dfw-discount-${gym.gym_id}`}>
          <Tag className="w-3.5 h-3.5 flex-shrink-0" /> {gym.discount_note}
        </p>
      )}

      {/* Pricing */}
      {gym.pricing?.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4" data-testid={`dfw-pricing-${gym.gym_id}`}>
          {gym.pricing.map((p) => {
            const chip = (
              <>
                <span className="text-white text-sm font-semibold">{p.price}</span>
                <span className="text-zinc-500 text-xs">{p.period}</span>
                <span className="block text-zinc-500 text-[10px] uppercase tracking-wider">{p.label}</span>
              </>
            );
            return p.url ? (
              <a
                key={p.label}
                href={p.url}
                target="_blank"
                rel="noopener noreferrer sponsored"
                className="px-3 py-1.5 bg-black/40 border border-zinc-800 hover:border-[#C7FA3E]/60 transition-colors"
                data-testid={`dfw-pricing-link-${gym.gym_id}-${p.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              >
                {chip}
              </a>
            ) : (
              <div key={p.label} className="px-3 py-1.5 bg-black/40 border border-zinc-800">{chip}</div>
            );
          })}
        </div>
      )}
      {gym.pricing_note && <p className="text-zinc-400 text-xs mb-4">{gym.pricing_note}</p>}

      <UpcomingClasses gym={gym} />

      {/* Limited-time event cards (auto-hidden after expiry) */}
      {gym.events?.filter((e) => !e.expires || new Date(e.expires) > new Date()).map((e) => (
        <div key={e.name} className="border border-[#C7FA3E]/30 bg-[#C7FA3E]/5 p-3 mb-3" data-testid={`dfw-event-${gym.gym_id}-${e.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
          <p className="text-[#C7FA3E] text-[10px] font-bold uppercase tracking-widest mb-0.5">Upcoming Event</p>
          <p className="text-white text-sm font-semibold" style={{ fontFamily: 'Oswald, sans-serif' }}>{e.name}</p>
          <p className="text-zinc-400 text-xs">{e.date}</p>
          {e.description && <p className="text-zinc-500 text-xs mt-1">{e.description}</p>}
          {e.url && (
            <a href={e.url} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 mt-2 px-3 py-1.5 bg-[#C7FA3E] text-black text-[11px] font-bold uppercase tracking-widest hover:opacity-90 transition-opacity"
              data-testid={`dfw-event-cta-${gym.gym_id}-${e.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
              {e.cta_label || 'Register'} <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
      ))}

      {/* CTAs */}
      <div className="mt-auto space-y-2">
        <div className="flex flex-wrap gap-2">
          {gym.buy_class_url && (
            <a
              href={gym.buy_class_url}
              target="_blank"
              rel="noopener noreferrer sponsored"
              className="flex-1 min-w-[140px] inline-flex items-center justify-center gap-1.5 px-4 py-2.5 bg-[#E63946] hover:bg-[#c62d39] text-white text-xs font-bold uppercase tracking-widest transition-colors"
              style={{ fontFamily: 'Oswald, sans-serif' }}
              data-testid={`dfw-buy-class-${gym.gym_id}`}
            >
              <Zap className="w-3.5 h-3.5" /> {gym.cta_label || 'Buy a Class'}
            </a>
          )}
          {gym.extra_ctas?.map((c) => (
            <a
              key={c.label}
              href={c.url}
              target="_blank"
              rel="noopener noreferrer sponsored"
              className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 border border-zinc-700 hover:border-zinc-500 text-zinc-300 text-xs uppercase tracking-widest transition-colors"
              data-testid={`dfw-extra-cta-${gym.gym_id}-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
            >
              {c.label} <ExternalLink className="w-3 h-3" />
            </a>
          ))}
          {gym.website && (
            <a
              href={gym.website}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 border border-zinc-700 hover:border-zinc-500 text-zinc-300 text-xs uppercase tracking-widest transition-colors"
              data-testid={`dfw-website-${gym.gym_id}`}
            >
              Website <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          {!gym.claimed && (
            <button
              onClick={() => onClaim(gym)}
              className="inline-flex items-center gap-1.5 text-zinc-500 hover:text-[#C7FA3E] text-xs transition-colors"
              data-testid={`dfw-claim-btn-${gym.gym_id}`}
            >
              <ShieldCheck className="w-3.5 h-3.5" /> Gym owner? Claim this listing — add pricing &amp; a buy-class link
            </button>
          )}
          {!hasMedia && (
            <button
              onClick={() => onAddVideo(gym)}
              className="inline-flex items-center gap-1.5 text-zinc-500 hover:text-[#C7FA3E] text-xs transition-colors"
              data-testid={`dfw-add-video-btn-${gym.gym_id}`}
            >
              <Video className="w-3.5 h-3.5" /> Own this gym? Add photos or a video
            </button>
          )}
        </div>
        <button
          onClick={scrollToRaceAlerts}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] uppercase tracking-wider border border-zinc-800 text-zinc-500 hover:text-[#C7FA3E] hover:border-[#C7FA3E]/40 transition-colors"
          data-testid={`race-alert-chip-${gym.gym_id}`}
        >
          <Bell className="w-3 h-3" /> Get race alerts
        </button>
      </div>

      {/* Location */}
      {(gym.address || gym.phone) && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-4 pt-3 border-t border-zinc-800/70 text-xs text-zinc-500">
          {gym.address && (
            <a href={mapsHref(gym.address)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:text-zinc-300 transition-colors">
              <MapPin className="w-3 h-3 text-[#E63946] flex-shrink-0" /> {gym.address}
            </a>
          )}
          {gym.phone && (
            <a href={`tel:${gym.phone.replace(/[^0-9+]/g, '')}`} className="inline-flex items-center gap-1 hover:text-zinc-300 transition-colors">
              <Phone className="w-3 h-3 flex-shrink-0" /> {gym.phone}
            </a>
          )}
        </div>
      )}

      <NearbyRecovery gymId={gym.gym_id} metro={locatorMetro} />
    </div>
  </div>
  );
};
